import { jobExternalId, jobTitleText, jobError } from "./jobFields";

/**
 * Column + presentation rules for Content Machine jobs.
 *
 * The machine owns `status`. mymediaplanner only mirrors it, so everything here
 * is derived — nothing writes back.
 */

// Column order matches the mockup: processing → downloaded → scripted → editing → done → error
export const JOB_COLUMNS = [
  { id: "processing", name: "Processing", statuses: ["queued", "downloading"] },
  { id: "downloaded", name: "Downloaded → script", statuses: ["downloaded"] },
  { id: "scripted", name: "Scripted → film", statuses: ["scripted"] },
  { id: "editing", name: "Editing", statuses: ["editing"] },
  { id: "done", name: "Done", statuses: ["done"] },
  { id: "error", name: "Needs attention", statuses: ["error"] },
];

const STATUS_TO_COLUMN = JOB_COLUMNS.reduce((acc, col) => {
  col.statuses.forEach((status) => {
    acc[status] = col.id;
  });
  return acc;
}, {});

export const columnIdForStatus = (status) =>
  STATUS_TO_COLUMN[status] ?? "processing";

export const groupJobsIntoColumns = (jobs = []) => {
  const columns = JOB_COLUMNS.reduce((acc, col) => {
    acc[col.id] = { name: col.name, items: [] };
    return acc;
  }, {});

  jobs.forEach((job) => {
    columns[columnIdForStatus(job?.status)].items.push(job);
  });

  return columns;
};

// Left border colour, mirroring the mockup: error red, scripted orange,
// otherwise by link_type (article green / video blue).
export const jobAccentColor = (job) => {
  if (job?.status === "error") return "#f46a6a";
  if (job?.status === "scripted") return "#f1b44c";
  if (job?.status === "done") return "#34c38f";
  if (job?.link_type === "article") return "#34c38f";
  return "#556ee6";
};

export const nextActionFor = (job) => {
  switch (job?.status) {
    case "queued":
      return { label: "→ waiting in queue", warn: false };
    case "downloading":
      return { label: "→ downloading…", warn: false };
    case "downloaded":
      return { label: "→ generate script", warn: false };
    case "scripted":
      return { label: "🎥 film it", warn: false };
    case "editing":
      return { label: "→ finish the edit", warn: false };
    case "done":
      return { label: "✓ done", warn: false };
    case "error":
      return {
        label: `⚠ ${jobError(job) || "check the source link"}`,
        warn: true,
      };
    default:
      return { label: job?.status ?? "—", warn: false };
  }
};

/**
 * The instructions, as a list of entries.
 *
 * Handles both shapes: the normalised `instructions` blob, and the on-disk
 * job.json with `instructions_text[]` plus `voice_notes[].transcribed`.
 *
 * @returns {Array<{text: string, voice: boolean}>}
 */
export const instructionList = (job) => {
  const raw = job?.raw ?? {};
  const entries = [];

  const typed = raw.instructions_text ?? job?.instructions_text;
  if (Array.isArray(typed)) {
    typed
      .map((entry) => String(entry).trim())
      .filter(Boolean)
      .forEach((text) => entries.push({ text, voice: false }));
  }

  const voiceNotes = raw.voice_notes ?? job?.voice_notes;
  if (Array.isArray(voiceNotes)) {
    voiceNotes
      .map((note) => String(note?.transcribed ?? "").trim())
      .filter(Boolean)
      .forEach((text) => entries.push({ text, voice: true }));
  }

  // Fall back to the combined blob only when neither array was present, so a
  // normalised payload does not get counted twice.
  if (entries.length === 0 && job?.instructions) {
    job.instructions
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((text) => entries.push({ text, voice: false }));
  }

  return entries;
};

export const instructionCount = (job) => instructionList(job).length;

export const shortId = (job) => {
  const external = jobExternalId(job);
  if (!external) return `#${job?.id ?? "?"}`;
  return `#${String(external).slice(-4)}`;
};

export const jobTitle = (job) => {
  const title = jobTitleText(job);
  if (title) return { text: title, muted: false };
  const platform = job?.raw?.download?.platform || job?.platform || "source";
  return { text: `${platform} ${job?.link_type ?? ""}`.trim(), muted: true };
};

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const total = Math.round(Number(seconds));
  if (Number.isNaN(total)) return null;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};
